/**
 * How the storage panel says what it knows, and what it does not.
 *
 * Separate from the markup because each of these is a sentence that can be
 * wrong in the reassuring direction, and a sentence that reassures wrongly is
 * this programme's most expensive defect. A row count is an estimate; a table
 * nobody has counted reports zero rows and is not empty; a single reading says
 * nothing at all about growth, which is the question somebody actually has.
 */

export interface TableReading {
  name: string
  estimated_rows: number
  bytes: number
  counted_at: string | null
  oldest?: string | null
  newest?: string | null
}

/** Bytes as a person reads them. Binary units, which is what the database and
 *  Azure both report, so a number here can be compared with a number there. */
export function readableSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const units = ['KiB', 'MiB', 'GiB', 'TiB']
  let value = bytes / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`
}

/**
 * What to put where the row count goes.
 *
 * A table that has never been analysed reports zero live rows, and so does an
 * empty one. Printing "0" for the first is a diagnostics page reporting an
 * empty journal over six months of settlement evidence, which is worse than
 * reporting nothing.
 */
export function readableCount(table: TableReading): string {
  // Two conditions guarding one render, and the second is not redundant.
  // PostgreSQL reports `reltuples` as -1, not 0, for a table it has never
  // analysed, and "about -1" on a diagnostics page is the kind of thing that
  // makes a reader distrust everything else on it. The two agree today, since
  // both come from the same ANALYZE, so this costs a comparison and removes a
  // dependency on them continuing to.
  if (table.counted_at === null || table.estimated_rows < 0) {
    return 'not counted yet'
  }
  return `about ${table.estimated_rows.toLocaleString('en-GB')}`
}


/**
 * How far back a table goes, as a date.
 *
 * The number a retention window is measured against: a window of ninety days
 * means nothing until somebody can see that the oldest row is eight months
 * old. A table with no time column says so rather than showing a blank, since
 * a blank cell reads as a missing value rather than an inapplicable question.
 */
export function holdsFrom(table: TableReading): string {
  if (!table.oldest) return 'no dated rows'
  return new Date(table.oldest).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Europe/Berlin',
  })
}
